import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AvisConstants } from "../../data/avisConstants";
import { Api } from "../../generated/Api";
import { OAuthResultViewModel } from "../../generated/data-contracts";
import { PortalType } from "../../models";
import { api, apiConfig } from "../../utils";
import { AvisLoader } from "../shared/components";
import { AvisContext } from "../shared/context/AvisContext";

interface IEIdRedirectPageProps {
  returnToPortal: PortalType;
  redirectType: "login" | "change-password";
}

export function EIdRedirectPage(props: Readonly<IEIdRedirectPageProps>) {
  const { redirectType, returnToPortal } = props;
  const [loading, setLoading] = useState(true);
  const { isAuthorized, updateUser } = useContext(AvisContext);

  //Initialize seperated api and axios instances to prevent global error handling
  const axiosInstance = axios.create(apiConfig);
  const apiInstance = new Api(apiConfig);

  const portalUrl = `${location.origin}/${returnToPortal}`;

  const getParamValues = (): OAuthResultViewModel | null => {
    const query = new URLSearchParams(location.search);
    if (query.size === 0) return null;

    return {
      code: query.get("code"),
      error: query.get("error"),
      errorDescription: query.get("error_description"),
      state: query.get("state"),
    } as OAuthResultViewModel;
  };

  useEffect(() => {
    if (!loading) setLoading(true);

    const params = getParamValues();

    if (redirectType === "login") {
      if (!params || params.error) {
        location.assign(`${portalUrl}${location.search}`);

        return;
      }

      axiosInstance
        .post("/Auth/login-eid", params, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
          },
        })
        .then(testAuthResult)
        .catch(() => {
          //If failed to autentificate with e-id, then same error as auth cancel #149732
          location.assign(`${portalUrl}?error=access_denied`);
        })
        .finally(() => setLoading(false));
    } else if (redirectType === "change-password") {
      if (!isAuthorized) location.assign(portalUrl);
      if (!params || params.error) {
        const passwordChangePage = `${portalUrl}${AvisConstants.url_change_password}${location.search}`;
        location.replace(passwordChangePage);
        return;
      }

      apiInstance
        .securityConfirmPasswordEId(params)
        .then((res) => {
          window.location.assign(res.data.redirectUrl + "?success=1");
        })
        .catch((err) => {
          const passwordChangePage = `${portalUrl}${AvisConstants.url_change_password}?server_error=${err.response.data.message}&server_error_code=${err.response.data.errorId}`;
          location.replace(passwordChangePage);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testAuthResult = () => {
    api.securityClaims().then((res) => {
      if (res.data && res.data.code) {
        updateUser(res.data);
        location.assign(portalUrl);

        return;
      }

      location.assign(`${portalUrl}?error=access_denied`);
    });
  };

  //This page always will redirect to login or changePassword pages
  return <AvisLoader />;
}
